// src/App.js
import React, { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./firebase/AuthContext";
import { DarkModeProvider } from "./context/DarkModeContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Toaster } from "react-hot-toast";
import Intro from "./components/Intro";
import Home, { preloadHomeData } from "./pages/Home";

// --- Lazy with Preload Helper ---
const lazyWithPreload = (factory) => {
  const Component = lazy(factory);
  Component.preload = factory;
  return Component;
};

// --- Lazy-loaded non-critical components ---
const Header = lazyWithPreload(() => import("./components/Header"));
const Footer = lazyWithPreload(() => import("./components/Footer"));
const Slider = lazyWithPreload(() => import("./components/Slider"));
const Login = lazyWithPreload(() => import("./pages/Login"));
const Signup = lazyWithPreload(() => import("./pages/Signup"));
const Dashboard = lazyWithPreload(() => import("./pages/Dashboard"));
const Deposit = lazyWithPreload(() => import("./pages/Deposit"));
const Withdrawal = lazyWithPreload(() => import("./pages/Withdrawal"));
const Profile = lazyWithPreload(() => import("./pages/Profile"));
const AdminRoute = lazyWithPreload(() => import("./components/AdminRoute"));
const AdminDeposits = lazyWithPreload(() => import("./pages/AdminDeposits"));
const AdminWithdrawals = lazyWithPreload(() => import("./pages/AdminWithdrawals"));
const AdminDashboard = lazyWithPreload(() => import("./pages/AdminDashboard"));
const PrivateRoute = lazyWithPreload(() => import("./components/PrivateRoute"));
const ProductsOwned = lazyWithPreload(() => import("./pages/ProductsOwned"));
const TransactionsHistory = lazyWithPreload(() => import("./pages/TransactionsHistory"));
const ReferralPage = lazyWithPreload(() => import("./pages/ReferralPage"));
const AboutPage = lazyWithPreload(() => import("./pages/AboutPage"));
const ReviewFormPage = lazyWithPreload(() => import("./pages/ReviewFormPage"));
const Terms = lazyWithPreload(() => import("./pages/Terms"));
const DisabledAccountPage = lazyWithPreload(() => import("./pages/DisabledAccountPage"));

// --- AppLayout Component ---
function AppLayout() {
  const location = useLocation();
  const hideFooter = ["/login", "/signup"].includes(location.pathname);

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Header />
      <Slider />

      {/* ✅ Add padding to avoid footer overlap */}
      <div className={!hideFooter ? "pb-20" : ""}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/deposit"
            element={
              <PrivateRoute>
                <Deposit />
              </PrivateRoute>
            }
          />
          <Route
            path="/withdrawal"
            element={
              <PrivateRoute>
                <Withdrawal />
              </PrivateRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <Profile />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/deposits"
            element={
              <AdminRoute>
                <AdminDeposits />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/withdrawals"
            element={
              <AdminRoute>
                <AdminWithdrawals />
              </AdminRoute>
            }
          />
          <Route
            path="/products-owned"
            element={
              <PrivateRoute>
                <ProductsOwned />
              </PrivateRoute>
            }
          />
          <Route
            path="/transactions-history"
            element={
              <PrivateRoute>
                <TransactionsHistory />
              </PrivateRoute>
            }
          />
          <Route
            path="/referral"
            element={
              <PrivateRoute>
                <ReferralPage />
              </PrivateRoute>
            }
          />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/review" element={<ReviewFormPage />} />
          <Route path="/terms" element={<Terms />} />

          {/* 🚨 Disabled page is public (so redirect works) */}
          <Route path="/disabled" element={<DisabledAccountPage />} />
        </Routes>
      </div>

      {/* ✅ Footer stays fixed but never covers content */}
      {!hideFooter && <Footer />}
    </Suspense>
  );
}

// --- InnerApp Component ---
function InnerApp({ showIntro, setShowIntro }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // ✅ Global redirect if user is disabled
    if (user && user.disabled === true) {
      navigate("/disabled", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    // Preload lazy components in the background
    Header.preload();
    Footer.preload();
    Slider.preload();
    Login.preload();
    Signup.preload();
    Dashboard.preload();
    Deposit.preload();
    Withdrawal.preload();
    Profile.preload();
    AdminRoute.preload();
    AdminDeposits.preload();
    AdminWithdrawals.preload();
    AdminDashboard.preload();
    PrivateRoute.preload();
    ProductsOwned.preload();
    TransactionsHistory.preload();
    ReferralPage.preload();
    AboutPage.preload();
    ReviewFormPage.preload();
    Terms.preload();
    DisabledAccountPage.preload();

    if (user) preloadHomeData(user);
  }, [user]);

  return showIntro ? (
    <Intro onFinish={() => setShowIntro(false)} />
  ) : (
    <AppLayout />
  );
}

// --- Main App ---
function App() {
  const [showIntro, setShowIntro] = useState(true);

  return (
    <Router>
      <AuthProvider>
        <DarkModeProvider>
          <InnerApp showIntro={showIntro} setShowIntro={setShowIntro} />
          <Toaster position="top-right" reverseOrder={false} />
          <ToastContainer position="top-right" autoClose={3000} />
        </DarkModeProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
